// AwnFormContainer.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { Workbook } from "exceljs";
import { animateScroll as scroll } from "react-scroll";
import api from "../services/api";
import { sendEmailNotification } from "../services/emailService";
import AwnFormUI from "./AwnFormUI"; // The presentational component

function AwnFormContainer() {
  const navigate = useNavigate();

  // --- All State Definitions ---
  const [submissionDate, setSubmissionDate] = useState(new Date());
  const [jobNumber, setJobNumber] = useState("");
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [projectType, setProjectType] = useState("");
  const [segmentName, setSegmentName] = useState("");
  const [researcherCharge, setResearcherCharge] = useState("");
  const [researcherCost, setResearcherCost] = useState("");
  const [finalQuestionnaire, setFinalQuestionnaire] = useState("");
  const [scirptingNeeded, setScirptingNeeded] = useState("");
  const [complexLogic, setComplexLogic] = useState("");
  const [sampleSize, setSampleSize] = useState("");
  const [dataCollection, setDataCollection] = useState("");
  const [haveS2CSub, setHaveS2CSub] = useState("");
  const [quota, setQuota] = useState("");
  const [quotaDetails, setQuotaDetails] = useState("");
  const [expectedFieldDate, setExpectedFieldDate] = useState("");
  const [expectedFinalLinkDate, setExpectedFinalLinkDate] = useState("");
  const [analysisPlan, setAnalysisPlan] = useState("");
  const [firstCutDate, setFirstCutDate] = useState("");
  const [dataWeightingRequired, setDataWeightingRequired] = useState("");
  const [sigTesting, setSigTesting] = useState("");
  const [spssOutput, setSpssOutput] = useState("");
  const [finalOutput, setFinalOutput] = useState("");
  const [weightingMatrix, setWeightingMatrix] = useState("");
  const [statementFF, setStatementFF] = useState("");
  const [maxdiffProgramming, setMaxdiffProgramming] = useState("");
  const [jaccards, setJaccards] = useState("");
  const [regression, setRegression] = useState("");
  const [krushal, setKrushal] = useState("");
  const [shapely, setShapely] = useState("");
  const [correlation, setCorrelation] = useState("");
  const [chaid, setChaid] = useState("");
  const [cluster, setCluster] = useState("");
  const [factor, setFactor] = useState("");
  const [bIP, setBIP] = useState("");
  const [dDV, setDDV] = useState("");
  const [dIV, setDIV] = useState("");
  const [numberOfAttributes, setNumberOfAttributes] = useState("");
  const [option1, setOption1] = useState(false);
  const [option2, setOption2] = useState(false);
  const [option3, setOption3] = useState(false);
  const [option4, setOption4] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Note: For researcherCharge to work correctly as a ForeignKey, the API
  // should ideally provide user IDs. The dropdown's value should be the ID.
  const [researchers, setResearchers] = useState([]); // Will store { id, name }

  // --- useEffect Hooks ---
  useEffect(() => {
    // Fetching user profiles for dropdowns
    api
      .get("users/names-departments/", {
        headers: {
          Authorization: `Token ${localStorage.getItem("Token")}`,
        },
      })
      .then((response) => {
        const profiles = response.data;
        // Assuming the API returns an array of objects like { id, name, department }
        const researchProfiles = profiles.filter(
          (p) => p.department === "Research"
        );
        setResearchers(researchProfiles);
      })
      .catch((error) => console.error("Error fetching researchers:", error));
  }, []);

  useEffect(() => {
    if (isSuccess || showAlert) {
      scroll.scrollToTop({ smooth: true });
    }
  }, [isSuccess, showAlert]);

  // --- All Handler Functions ---
  const handleJob = (e) => {
    const value = e.target.value;
    if (/^[A-Za-z]{0,2}$|^[A-Za-z]{2}\d*$/.test(value)) {
      setJobNumber(value);
    }
  };

  const handleSampleSizeChange = (e) => {
    if (/^\d*$/.test(e.target.value)) {
      setSampleSize(e.target.value);
    }
  };

  const handleNoaChange = (e) => {
    if (/^\d*$/.test(e.target.value)) {
      setNumberOfAttributes(e.target.value);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    try {
      // Handles both Date objects and date strings
      return new Date(date).toISOString().split("T")[0];
    } catch (e) {
      console.error("Invalid date provided to formatDate:", date);
      return null;
    }
  };

  // =================================================================
  //  UPDATED AND FINAL handleSubmit FUNCTION
  // =================================================================
  const handleSubmit = async () => {
    setErrorMessage("");
    const newErrors = {};
    let errorMessages = [];

    // --- Validation Helpers ---
    const validateField = (value, name, fieldKey) => {
      if (!value || (typeof value === "string" && !value.trim())) {
        newErrors[fieldKey] = `${name} is required.`;
        errorMessages.push(`${name} is required.`);
      }
    };
    const validateBooleanField = (value, name, fieldKey) => {
      if (value !== "Yes" && value !== "No") {
        newErrors[fieldKey] = `${name} must be selected.`;
        errorMessages.push(`${name} must be selected.`);
      }
    };
    const validateDate = (date, name, fieldKey) => {
      if (!date) {
        newErrors[fieldKey] = `${name} is required.`;
        errorMessages.push(`${name} is required.`);
      } else if (isNaN(Date.parse(date))) {
        newErrors[fieldKey] = `${name} must be a valid date.`;
        errorMessages.push(`${name} must be a valid date.`);
      }
    };

    // --- Run All Validations ---
    validateField(jobNumber, "Job Number", "jobNumber");
    validateField(projectName, "Project Name", "projectName");
    validateField(clientName, "Client Name", "clientName");
    validateField(projectType, "Project Type", "projectType");
    validateField(researcherCharge, "Researcher In Charge", "researcherCharge");
    validateField(researcherCost, "Researcher Cost Center", "researcherCost");
    validateField(sampleSize, "Sample Size", "sampleSize");
    validateField(dataCollection, "Data Collection Method", "dataCollection");
    validateDate(finalQuestionnaire, "Final Questionnaire Date", "finalQuestionnaire");
    validateDate(expectedFieldDate, "Expected Field Work Completion Date", "expectedFieldDate");
    validateDate(analysisPlan, "Analysis Plan Date", "analysisPlan");
    validateDate(firstCutDate, "First Cut Date", "firstCutDate");
    validateDate(finalOutput, "Final Output Date", "finalOutput");
    validateBooleanField(scirptingNeeded, "Scripting Needed", "scirptingNeeded");
    validateBooleanField(complexLogic, "Complex Logic", "complexLogic");
    validateBooleanField(haveS2CSub, "Have S2C Subscription", "haveS2CSub");
    validateBooleanField(quota, "Quota", "quota");
    validateBooleanField(dataWeightingRequired, "Data Weighting Required", "dataWeightingRequired");
    validateBooleanField(sigTesting, "Sig Testing", "sigTesting");
    validateBooleanField(spssOutput, "SPSS Output", "spssOutput");
    validateBooleanField(statementFF, "Statement Fixed Format", "statementFF");
    validateBooleanField(maxdiffProgramming, "MAXDIFF Programming", "maxdiffProgramming");
    validateBooleanField(jaccards, "Jaccards", "jaccards");
    validateBooleanField(regression, "Regression", "regression");
    validateBooleanField(krushal, "Krushal", "krushal");
    validateBooleanField(shapely, "Shapely", "shapely");
    validateBooleanField(correlation, "Correlation", "correlation");
    validateBooleanField(chaid, "CHAID", "chaid");
    validateBooleanField(cluster, "Cluster", "cluster");
    validateBooleanField(factor, "Factor", "factor");
    validateBooleanField(bIP, "BIP", "bIP");

    if (quota === "Yes") validateDate(quotaDetails, "Quota Details Date", "quotaDetails");
    if (dataWeightingRequired === "Yes") validateDate(weightingMatrix, "Weighting Matrix Date", "weightingMatrix");
    if (maxdiffProgramming === "Yes") validateField(numberOfAttributes, "Maxdiff - Number of Attributes", "numberOfAttributes");
    if (sigTesting === "Yes" && !option1 && !option2 && !option3 && !option4) {
      newErrors.options = "At least one Sig Testing option must be selected.";
      errorMessages.push("At least one Sig Testing option must be selected.");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setErrorMessage("Please fix the validation errors before submitting.");
      setShowAlert(true);
      scroll.scrollToTop();
      return;
    }

    setErrors({});
    setShowAlert(false);

    // This single payload contains all data for both models
    const fullPayload = {
      date_of_awn: formatDate(submissionDate),
      job_number: jobNumber,
      project_name: projectName,
      unit: researcherCost,
      segment_name: segmentName || projectName,
      actual_sample_size: sampleSize,
      client: clientName,
      expected_date_of_final_output: formatDate(finalOutput),
      submissionDate: formatDate(submissionDate),
      jobNumber: jobNumber,
      projectName: projectName,
      projectType: projectType,
      researcherCharge: researcherCharge, // This should be the user's ID
      researcherCost: researcherCost,
      finalQuestionnaire: formatDate(finalQuestionnaire),
      scirptingNeeded: scirptingNeeded === 'Yes',
      complexLogic: complexLogic === 'Yes',
      sampleSize: parseInt(sampleSize, 10),
      dataCollection: dataCollection,
      haveS2CSub: haveS2CSub === 'Yes',
      quota: quota === 'Yes',
      quotaDetails: formatDate(quotaDetails),
      expectedFieldDate: formatDate(expectedFieldDate),
      expectedFinalLinkDate: formatDate(expectedFinalLinkDate),
      analysisPlan: formatDate(analysisPlan),
      firstCutDate: formatDate(firstCutDate),
      dataWeightingRequired: dataWeightingRequired === 'Yes',
      sigTesting: sigTesting === 'Yes',
      spssOutput: spssOutput === 'Yes',
      finalOutput: formatDate(finalOutput),
      weightingMatrix: formatDate(weightingMatrix),
      statementFF: statementFF === 'Yes',
      maxdiffProgramming: maxdiffProgramming === 'Yes',
      jaccards: jaccards === 'Yes',
      regression: regression === 'Yes',
      krushal: krushal === 'Yes',
      shapely: shapely === 'Yes',
      correlation: correlation === 'Yes',
      chaid: chaid === 'Yes',
      cluster: cluster === 'Yes',
      factor: factor === 'Yes',
      bIP: bIP === 'Yes',
      dDV: dDV,
      dIV: dIV,
      numberOfAttributes: numberOfAttributes ? parseInt(numberOfAttributes, 10) : null,
      option1: option1,
      option2: option2,
      option3: option3,
      option4: option4,
    };

    try {
      const response = await api.post(
        "api/awn/create-segment-entry/",
        fullPayload,
        {
          headers: { Authorization: `Token ${localStorage.getItem("Token")}` },
        }
      );

      console.log("Entry Created Successfully:", response.data);
      
      setIsSubmitted(true);
      setIsSuccess(true);
      sendEmailNotification({ projectName, jobNumber }).catch(console.error);

      if (response.data.warning) {
        setErrorMessage(response.data.warning);
        setShowAlert(true);
      } else {
        scroll.scrollToTop();
      }

    } catch (error) {
      console.error("Error in Submission:", error);
      const serverErrors = error.response?.data;
      if (typeof serverErrors === 'object' && serverErrors !== null) {
        setErrorMessage("Validation failed. Please correct the highlighted fields.");
        setErrors(serverErrors);
      } else {
        setErrorMessage(serverErrors || "An error occurred during submission.");
      }
      setShowAlert(true);
    }
  };

  const handleDownload = async () => {
    try {
        const response = await fetch("/4SiGHT_Analysis_Warning_Note.xlsx");
        if (!response.ok) {
            throw new Error(`Could not fetch the Excel template. Status: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        
        const workbook = new Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.worksheets[0];

        const formatDateForExcel = (date) => {
            if (!date) return "";
            try {
                const d = new Date(date);
                const day = String(d.getDate()).padStart(2, '0');
                const month = d.toLocaleString('en-GB', { month: 'short' });
                const year = d.getFullYear();
                return `${day}-${month}-${year}`;
            } catch {
                return "";
            }
        };

        const setCellValueAndAlignRight = (cell, value) => {
            worksheet.getCell(cell).value = value;
            worksheet.getCell(cell).alignment = { horizontal: "right" };
        };

        const selectedOptions = [];
        if (option1) selectedOptions.push("☑95%");
        if (option2) selectedOptions.push("☑90%");
        if (option3) selectedOptions.push("☑80%");
        if (option4) selectedOptions.push("☑67%");
        const selectedOptionsString = selectedOptions.join(", ");

        worksheet.getCell("C6").value = submissionDate;
        worksheet.getCell("C7").value = jobNumber;
        worksheet.getCell("C8").value = projectName;
        worksheet.getCell("C12").value = clientName;
        worksheet.getCell("C9").value = projectType;
        worksheet.getCell("C10").value = researchers.find(r => String(r.id) === String(researcherCharge))?.name || researcherCharge;
        worksheet.getCell("C11").value = researcherCost;
        setCellValueAndAlignRight("C14", formatDateForExcel(finalQuestionnaire));
        worksheet.getCell("C15").value = scirptingNeeded;
        worksheet.getCell("F15").value = complexLogic;
        worksheet.getCell("C16").value = sampleSize;
        worksheet.getCell("C17").value = dataCollection;
        worksheet.getCell("C18").value = haveS2CSub;
        setCellValueAndAlignRight("C26", formatDateForExcel(firstCutDate));
        worksheet.getCell("C28").value = dataWeightingRequired;
        worksheet.getCell("C30").value = sigTesting;
        worksheet.getCell("C32").value = spssOutput;
        setCellValueAndAlignRight("F26", formatDateForExcel(finalOutput));
        setCellValueAndAlignRight("F28", formatDateForExcel(weightingMatrix));
        worksheet.getCell("F32").value = statementFF;
        worksheet.getCell("C19").value = quota;
        setCellValueAndAlignRight("C20", formatDateForExcel(quotaDetails));
        setCellValueAndAlignRight("C21", formatDateForExcel(expectedFieldDate));
        worksheet.getCell("E21").value = "Expected Final Link";
        setCellValueAndAlignRight("F21", formatDateForExcel(expectedFinalLinkDate));
        setCellValueAndAlignRight("C22", formatDateForExcel(analysisPlan));
        worksheet.getCell("E30").value = selectedOptionsString;
        worksheet.getCell("C35").value = maxdiffProgramming;
        worksheet.getCell("C37").value = numberOfAttributes;
        worksheet.getCell("C42").value = jaccards;
        worksheet.getCell("C43").value = regression;
        worksheet.getCell("C44").value = krushal;
        worksheet.getCell("C45").value = shapely;
        worksheet.getCell("C46").value = correlation;
        worksheet.getCell("C47").value = chaid;
        worksheet.getCell("C49").value = cluster;
        worksheet.getCell("C50").value = factor;
        worksheet.getCell("C51").value = bIP;
        worksheet.getCell("F42").value = dDV;
        worksheet.getCell("F44").value = dIV;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `4SiGHT_Analysis_Warning Note_${projectName}.xlsx`);
        navigate("/projects_list");

    } catch (error) {
        console.error("Failed to download Excel file:", error);
        setErrorMessage("Failed to generate the Excel file. Please try again.");
        setShowAlert(true);
    }
  };

  const formProps = {
    isSubmitted, isSuccess, showAlert, errorMessage, errors,
    submissionDate, jobNumber, projectName, clientName, projectType, segmentName, researcherCharge, researcherCost,
    finalQuestionnaire, scirptingNeeded, complexLogic, sampleSize, dataCollection, haveS2CSub, quota, quotaDetails,
    expectedFieldDate, expectedFinalLinkDate, analysisPlan, firstCutDate, dataWeightingRequired, sigTesting, spssOutput, finalOutput,
    weightingMatrix, statementFF, maxdiffProgramming, jaccards, regression, krushal, shapely, correlation, chaid,
    cluster, factor, bIP, dDV, dIV, numberOfAttributes, option1, option2, option3, option4,
    researchers, // Pass the list of researcher profiles
    
    setIsSuccess, setShowAlert, setSubmissionDate, setJobNumber, handleJob, setProjectName, setClientName, setProjectType,
    setSegmentName, setResearcherCharge, setResearcherCost, setFinalQuestionnaire, setScirptingNeeded, setComplexLogic,
    setSampleSize, handleSampleSizeChange, setDataCollection, setHaveS2CSub, setQuota, setQuotaDetails, setExpectedFieldDate,
    setExpectedFinalLinkDate, setAnalysisPlan, setFirstCutDate, setDataWeightingRequired, setSigTesting, setOption1, setOption2, setOption3, setOption4,
    setSpssOutput, setFinalOutput, setWeightingMatrix, setStatementFF, setMaxdiffProgramming, handleNoaChange, setJaccards,
    setRegression, setKrushal, setShapely, setCorrelation, setChaid, setCluster, setFactor, setBIP, setDDV, setDIV,
    handleSubmit, handleDownload,
  };

  return <AwnFormUI {...formProps} />;
}

export default AwnFormContainer;
